import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCog, Printer } from "lucide-react";
import { NIVEL_EDUCATIVO_911, FUNCION_PERSONAL_911, labelFuncion, labelNivel } from "@/lib/sepCatalogos";
import { parseCURP, isValidCURP } from "@/lib/curp";

const ESTADOS_CIVIL = ["SOLTERO(A)", "CASADO(A)", "UNIÓN LIBRE", "DIVORCIADO(A)", "VIUDO(A)"];
const DISC_OPTS = ["CEGUERA", "BAJA VISIÓN", "SORDERA", "HIPOACUSIA", "MOTRIZ", "INTELECTUAL", "PSICOSOCIAL", "OTRA"];

const emptyForm = {
  nombre: "", apellido_paterno: "", apellido_materno: "", sexo: "H",
  funcion: FUNCION_PERSONAL_911[0].value,
  nivel_educativo: NIVEL_EDUCATIVO_911[0].value,
  nivel_educativo_otros: "",
  curp: "", rfc: "", celular: "", correo_personal: "", fecha_nacimiento: "",
  estado_civil: "", plaza: "", fecha_ingreso_sep: "", discapacidad: "",
  lenguas_indigenas: "", horas_semanales: 0,
};

const FUNCIONES_CON_HORAS = new Set([
  "PROFESOR_EDUCACION_FISICA", "PROFESOR_ARTISTICAS",
  "PROFESOR_TECNOLOGICAS", "PROFESOR_IDIOMAS",
]);
const showHoras = (f) => FUNCIONES_CON_HORAS.has(f);

export default function MiPersonal() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rfcManual, setRfcManual] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/mi-personal");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setRfcManual(false);
    setOpen(true);
  };

  const startEdit = (it) => {
    setEditing(it);
    setForm({
      nombre: it.nombre || "", apellido_paterno: it.apellido_paterno || "",
      apellido_materno: it.apellido_materno || "", sexo: it.sexo || "H",
      funcion: it.funcion || FUNCION_PERSONAL_911[0].value,
      nivel_educativo: it.nivel_educativo || NIVEL_EDUCATIVO_911[0].value,
      nivel_educativo_otros: it.nivel_educativo_otros || "",
      curp: it.curp || "", rfc: it.rfc || "", celular: it.celular || "",
      correo_personal: it.correo_personal || "", fecha_nacimiento: it.fecha_nacimiento || "",
      estado_civil: it.estado_civil || "", plaza: it.plaza || "",
      fecha_ingreso_sep: it.fecha_ingreso_sep || "", discapacidad: it.discapacidad || "",
      lenguas_indigenas: it.lenguas_indigenas || "", horas_semanales: it.horas_semanales || 0,
    });
    const rfc0 = (it.rfc || "").toUpperCase();
    const curp0 = (it.curp || "").toUpperCase();
    setRfcManual(!!rfc0 && (curp0.length < 10 || rfc0 !== curp0.slice(0, 10)));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.curp && !isValidCURP(form.curp)) {
      toast.error("La CURP debe tener 18 caracteres con formato válido.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.nivel_educativo !== "OTROS") payload.nivel_educativo_otros = "";
      if (editing) {
        await api.put(`/mi-personal/${editing.id}`, payload);
        toast.success("Registro actualizado");
      } else {
        await api.post("/mi-personal", payload);
        toast.success("Personal registrado");
      }
      setOpen(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/mi-personal/${confirm.id}`);
      toast.success("Registro eliminado");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Personal a mi cargo</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Mi Personal por Función
            </h1>
            <p className="text-sm text-[#545454] mt-1">
              Personal administrativo/técnico bajo su responsabilidad directa. Independiente del reporte 911 de las escuelas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/mi-personal/print" target="_blank">
              <Button variant="outline" data-testid="btn-print-mi-personal">
                <Printer className="w-4 h-4 mr-1" /> Imprimir / PDF
              </Button>
            </Link>
            <Button data-testid="btn-add-mi-personal" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo registro
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Nombre completo</TableHead>
                <TableHead className="w-16">Sexo</TableHead>
                <TableHead>Función</TableHead>
                <TableHead>Nivel educativo</TableHead>
                <TableHead>CURP</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <UserCog className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                    <div className="text-sm text-[#545454]">Sin personal registrado</div>
                  </TableCell>
                </TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`mp-row-${it.id}`}>
                  <TableCell className="font-medium">
                    {`${it.nombre} ${it.apellido_paterno} ${it.apellido_materno}`.trim() || "—"}
                  </TableCell>
                  <TableCell className="text-center">{it.sexo}</TableCell>
                  <TableCell>{labelFuncion(it.funcion)}</TableCell>
                  <TableCell>
                    {labelNivel(it.nivel_educativo)}
                    {it.nivel_educativo === "OTROS" && it.nivel_educativo_otros && (
                      <span className="text-xs text-[#6B7280] ml-1">({it.nivel_educativo_otros})</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{it.curp || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`btn-edit-mp-${it.id}`} variant="ghost" size="sm" onClick={() => startEdit(it)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`btn-del-mp-${it.id}`} variant="ghost" size="sm" onClick={() => setConfirm(it)}>
                      <Trash2 className="w-4 h-4 text-[#9F2241]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar personal" : "Nuevo personal"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre(s) *</Label>
              <Input data-testid="mp-nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Apellido paterno *</Label>
              <Input data-testid="mp-ap" required value={form.apellido_paterno} onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Apellido materno</Label>
              <Input value={form.apellido_materno} onChange={(e) => setForm({ ...form, apellido_materno: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sexo *</Label>
              <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                <SelectTrigger data-testid="mp-sexo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="H">Hombre</SelectItem>
                  <SelectItem value="M">Mujer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Función *</Label>
              <Select value={form.funcion} onValueChange={(v) => setForm({ ...form, funcion: v, horas_semanales: showHoras(v) ? form.horas_semanales : 0 })}>
                <SelectTrigger data-testid="mp-funcion"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {FUNCION_PERSONAL_911.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {showHoras(form.funcion) && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Horas semanales</Label>
                <Input type="number" min={0} data-testid="mp-horas"
                  value={form.horas_semanales || 0}
                  onChange={(e) => setForm({ ...form, horas_semanales: parseInt(e.target.value || "0", 10) || 0 })} />
              </div>
            )}
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Nivel educativo *</Label>
              <Select value={form.nivel_educativo} onValueChange={(v) => setForm({ ...form, nivel_educativo: v })}>
                <SelectTrigger data-testid="mp-nivel-educativo"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {NIVEL_EDUCATIVO_911.map((n) => (
                    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.nivel_educativo === "OTROS" && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Especifique *</Label>
                <Input data-testid="mp-nivel-otros" value={form.nivel_educativo_otros}
                  onChange={(e) => setForm({ ...form, nivel_educativo_otros: e.target.value.toUpperCase() })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">CURP</Label>
              <Input data-testid="mp-curp" value={form.curp} maxLength={18} className="font-mono"
                onChange={(e) => {
                  const curp = e.target.value.toUpperCase();
                  const parsed = parseCURP(curp);
                  const rfcAutoPrefix = curp.length >= 10 ? curp.slice(0, 10) : "";
                  setForm((f) => ({
                    ...f, curp,
                    fecha_nacimiento: parsed?.fecha_nacimiento || f.fecha_nacimiento,
                    sexo: parsed?.genero || f.sexo,
                    rfc: !rfcManual && rfcAutoPrefix ? rfcAutoPrefix : f.rfc,
                  }));
                }} />
              {form.curp && form.curp.length > 0 && !isValidCURP(form.curp) && (
                <p className="text-[11px] text-[#9F2241]" data-testid="mp-curp-error">
                  Formato de CURP inválido.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RFC</Label>
              <Input data-testid="mp-rfc" value={form.rfc} maxLength={13} className="font-mono"
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setRfcManual(val.length > 0);
                  setForm({ ...form, rfc: val });
                }} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha de nacimiento</Label>
              <Input type="date" data-testid="mp-fecha-nac" value={form.fecha_nacimiento || ""}
                onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                readOnly={!!parseCURP(form.curp)?.fecha_nacimiento}
                className={parseCURP(form.curp)?.fecha_nacimiento ? "bg-[#F3F4F6] cursor-not-allowed" : ""} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Estado civil</Label>
              <Select value={form.estado_civil || "__none__"} onValueChange={(v) => setForm({ ...form, estado_civil: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="mp-estado-civil"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin especificar —</SelectItem>
                  {ESTADOS_CIVIL.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Celular</Label>
              <Input data-testid="mp-celular" value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Correo personal</Label>
              <Input data-testid="mp-correo" type="email" value={form.correo_personal} onChange={(e) => setForm({ ...form, correo_personal: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Plaza / Clave</Label>
              <Input data-testid="mp-plaza" value={form.plaza} onChange={(e) => setForm({ ...form, plaza: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha de ingreso al servicio</Label>
              <Input type="date" data-testid="mp-fecha-ingreso" value={form.fecha_ingreso_sep || ""}
                onChange={(e) => setForm({ ...form, fecha_ingreso_sep: e.target.value })} />
            </div>
            <DialogFooter className="md:col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-mp" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-del-mp" onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
